import React, { ReactNode, useEffect, useState } from 'react';
import { GetServerSideProps, NextPage } from 'next';
import { Box, OParagraph, defaultColors } from '@lifechurch/react-ion';
import { useAdminOverviewConnectionLazyQuery } from '@generated/types';

import AnalyticsTabs from '@components/Analytics/AnalyticsTabs';
import { PageHeader } from '@components/PageHeader';
import { RangePicker } from '@components/RangePicker';

import AnalyticsOverviewGrid from '@components/Analytics/Overview/AnalyticsOverviewGrid';

import dayjs from 'dayjs';
import { useAnalyticsOverviewContext } from '@lib/servicesContext';
import { LoadingScreen } from '@components/LoadingScreen';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { DEFAULT_LOCALE } from '@utils/constants';
import { useTranslation } from 'react-i18next';
import { useDayjsLocale } from '@churchonline/hooks';
import { useRouter } from 'next/router';

const Overview: NextPage = () => {
  const { from: ctxFrom, to: ctxTo, updateRange } = useAnalyticsOverviewContext();
  const [currentServices, { loading, data }] = useAdminOverviewConnectionLazyQuery({
    fetchPolicy: 'cache-first',
    variables: {
      filter: {
        hasContent: true,
      },
    },
  });
  const { t } = useTranslation('analytics_overview');
  const { locale } = useRouter();

  useDayjsLocale(locale as string);

  const [hasServicesInRange, setHasServicesInRange] = useState<boolean>();

  useEffect(() => {
    currentServices({
      variables: {
        from: ctxFrom,
        to: ctxTo,
      },
    });
  }, [ctxFrom, ctxTo]);

  useEffect(() => {
    if (data?.currentOrganization?.servicesConnection?.aggregate) {
      const servicesLength = data.currentOrganization.servicesConnection.aggregate.count;
      setHasServicesInRange(servicesLength > 0);
    }
  }, [data]);

  const updateDateRange = (from?: string, to?: string): void => {
    from && to && updateRange(from, to);
  };

  const renderContent = (): ReactNode => {
    if (loading) {
      return <LoadingScreen />;
    } else if (hasServicesInRange === false && !loading) {
      return (
        <Box display='flex' flexDirection='column' data-testid='noAnalytics' marginTop='48px'>
          <Box margin='0 auto'>
            <img
              data-testid='analyticsImage'
              src={`/admin/static/analytics_overview_image.png`}
              alt={t('analytics')}
              width={302}
              height={200}
            />
          </Box>
          <Box
            maxWidth='488px'
            textAlign='center'
            paddingBlockStart='16px'
            paddingBlockEnd='0'
            paddingInlineStart='0'
            paddingInlineEnd='0'
            margin='0 auto'
          >
            <OParagraph>{t('empty_overview')}</OParagraph>
          </Box>
        </Box>
      );
    } else if (hasServicesInRange === false) {
      return (
        <>
          <Box display={' '} marginBlockEnd={5}>
            <RangePicker
              from={new Date(ctxFrom)}
              to={new Date(ctxTo)}
              onClick={updateDateRange}
              data-testid='datepicker'
              needPastDates
              // TODO: Determine the correct cut-off date for grabing data
              fromMonth={new Date(dayjs().subtract(1, 'year').format())}
              disabledDays={[
                {
                  after: new Date(),
                },
              ]}
            />
          </Box>
          <Box marginBlockStart={8} textAlign='center' color={defaultColors.gray50}>
            {t('no_service')}
          </Box>
        </>
      );
    } else {
      if (hasServicesInRange && data?.currentOrganization && !loading) {
        return (
          <>
            <Box display={' '} marginBlockEnd={5}>
              <RangePicker
                from={new Date(ctxFrom)}
                to={new Date(ctxTo)}
                onClick={updateDateRange}
                data-testid='datepicker'
                needPastDates
                // TODO: Determine the correct cut-off date for grabing data
                fromMonth={new Date(dayjs().subtract(1, 'year').format())}
                disabledDays={[
                  {
                    after: new Date(),
                  },
                ]}
              />
            </Box>

            <AnalyticsOverviewGrid
              analyticsOverview={data.currentOrganization.analyticsOverview}
              servicesConnection={data.currentOrganization.servicesConnection}
            />
          </>
        );
      }
    }
  };

  return (
    <Box>
      <PageHeader title={t('analytics')} />
      <AnalyticsTabs activeId={'overview'} />
      {renderContent()}
    </Box>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ locale = DEFAULT_LOCALE }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['common', 'analytics_overview'])),
    backgroundColor: defaultColors.gray5,
  },
});

export default Overview;
